import { z } from "zod";
import { recordIdSchema } from "../values/record-id";
import { nullableDecimalSchema, type DecimalValue } from "../values/decimal";

import {
  DELIVERY_ENTRY_FIELDS, deliveryEntryPath, deliveryScheduleIssues, deliveryScheduleSchema, orderQuantityOf, type DeliveryEntry,
} from "./order-delivery-schedule";
import { lifecycleDateIssues } from "./order-lifecycle-policy";
import { rejectLegacyOrderFields } from "./order-input-contract";
import { ORDER_INPUT_COPY } from "./order-input-copy";
import { ORDER_DIRECTIONS, orderDirectionSchema, orderStatusSchema } from "./order-types";

const createOrderShape = {
  chemicalId: z.coerce.number().int().positive().nullable().optional(),
  chemicalName: z.string().trim().nullable().optional(),
  casNumber: z.string().trim().nullable().optional(),
  contactId: z.coerce.number().int().positive(),
  direction: orderDirectionSchema,
  unit: z.string().trim().min(1),
  pricePerItem: nullableDecimalSchema.optional(),
  internalProductNumber: z.string().trim().nullable().optional(),
  externalProductNumber: z.string().trim().nullable().optional(),
  referenceNumber: z.string().trim().nullable().optional(),
  customerPoNumber: z.string().trim().nullable().optional(),
  contactAddressId: recordIdSchema.nullable().optional(),
  requestedBy: z.coerce.number().int().positive().nullable().optional(),
  customerRequestorContactPersonId: z.coerce.number().int().positive().nullable().optional(),
  deliverySchedule: deliveryScheduleSchema,
  notes: z.string().nullable().optional(),
};

function withLineQuantity<T extends { deliverySchedule: DeliveryEntry[] }>(data: T) {
  return { ...data, quantity: orderQuantityOf(data.deliverySchedule) };
}

function withAmendedLineQuantity<T extends { deliverySchedule?: DeliveryEntry[] }>(data: T): T & { quantity?: DecimalValue } {
  return data.deliverySchedule ? { ...data, quantity: orderQuantityOf(data.deliverySchedule) } : data;
}

export type SemanticOrderInput = {
  chemicalId?: number | null;
  chemicalName?: string | null;
  casNumber?: string | null;
  direction: "purchase" | "sale";
  requestedBy?: number | null;
  customerRequestorContactPersonId?: number | null;
  submittedAt?: Date | null;
  deliverySchedule?: DeliveryEntry[];
};

export function validateOrderInput(data: SemanticOrderInput, ctx: z.RefinementCtx) {
  for (const issue of deliveryScheduleIssues(data.submittedAt, data.deliverySchedule ?? [])) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue.message, path: deliveryEntryPath(issue.index, DELIVERY_ENTRY_FIELDS.date) });
  }
  if (!data.chemicalId && !data.chemicalName?.trim() && !data.casNumber?.trim()) {
    const message = ORDER_INPUT_COPY.issues.chemicalRequired;
    ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ["chemicalName"] });
    ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ["casNumber"] });
  }
  if (data.direction === ORDER_DIRECTIONS.purchase && !data.requestedBy) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: ORDER_INPUT_COPY.issues.purchaseRequestedBy, path: ["requestedBy"] });
  }
  if (data.direction === ORDER_DIRECTIONS.purchase && data.customerRequestorContactPersonId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: ORDER_INPUT_COPY.issues.purchaseNoCustomerRequestor, path: ["customerRequestorContactPersonId"] });
  }
  if (data.direction === ORDER_DIRECTIONS.sale && data.requestedBy) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: ORDER_INPUT_COPY.issues.saleNoInternalRequestor, path: ["requestedBy"] });
  }
}

export function firstOrderInputIssue(data: SemanticOrderInput): string | null {
  let message: string | null = null;
  validateOrderInput(data, {
    addIssue: (issue) => { message ??= issue.message ?? ORDER_INPUT_COPY.issues.invalid; },
  } as z.RefinementCtx);
  return message;
}

export const createOrderSchema = z.preprocess(rejectLegacyOrderFields, z.object({
  ...createOrderShape,
  orderStatus: orderStatusSchema,
  submittedAt: z.coerce.date(),
  orderedAt: z.coerce.date().nullable().optional(),
  arrivedDate: z.coerce.date().nullable().optional(),
}).strict().superRefine((data, ctx) => {
  validateOrderInput(data, ctx);
  for (const issue of lifecycleDateIssues({
    status: data.orderStatus,
    submittedAt: data.submittedAt ?? null,
    orderedAt: data.orderedAt ?? null,
    arrivedDate: data.arrivedDate ?? null,
  })) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue.message, path: [issue.field] });
  }
}).transform(withLineQuantity));

export const editOrderSchema = z.preprocess(rejectLegacyOrderFields, z.object({
  direction: orderDirectionSchema.optional(),
  contactId: z.coerce.number().int().positive().optional(),
  chemicalName: z.string().trim().nullable().optional(),
  casNumber: z.string().trim().nullable().optional(),
  unit: z.string().trim().min(1).optional(),
  pricePerItem: createOrderShape.pricePerItem,
  internalProductNumber: z.string().trim().nullable().optional(),
  externalProductNumber: z.string().trim().nullable().optional(),
  referenceNumber: z.string().trim().nullable().optional(),
  customerPoNumber: z.string().trim().nullable().optional(),
  contactAddressId: recordIdSchema.nullable().optional(),
  requestedBy: z.coerce.number().int().positive().nullable().optional(),
  customerRequestorContactPersonId: z.coerce.number().int().positive().nullable().optional(),
  deliverySchedule: createOrderShape.deliverySchedule.optional(),
  notes: z.string().nullable().optional(),
}).transform(withAmendedLineQuantity));

export const cancelOrderSchema = z.object({
  cancellationReason: z.string().trim().nullable().optional(),
});

export type CreateOrderInput = Omit<z.infer<typeof createOrderSchema>, "quantity" | "deliverySchedule"> & {
  quantity: DecimalValue; deliverySchedule: DeliveryEntry[];
};
export type EditOrderInput = Omit<z.infer<typeof editOrderSchema>, "quantity" | "deliverySchedule"> & {
  quantity?: DecimalValue; deliverySchedule?: DeliveryEntry[];
};
export type CancelOrderInput = z.infer<typeof cancelOrderSchema>;
